import Vue from 'vue';
import { apolloProvider, httpApolloProvider } from '@/graphql/vueApollo'; // GraphQL connection
import EventBus from '@/utilities/eventBus';
import logger from 'client-logger';

import { FORBIDDEN } from '@/utilities/errorCodes';

// Queries
import GET_STORE_BY_ID from '@/graphql/queries/getStoreById.gql';
import GET_USER_STORES from '@/graphql/queries/getUserStores.gql';
import GET_COMPANY_BY_ID from '@/graphql/queries/getCompanyById.gql';

// Mutations
import UPDATE_STORE_INFO from '@/graphql/mutations/updateStoreInfo.gql';
import UPDATE_SHIPPING_ADDRESS from '@/graphql/mutations/updateShippingAddress.gql';
import UPDATE_BILLING_ADDRESS from '@/graphql/mutations/updateBillingAddress.gql';
import DELETE_STORE_BY_ID from '@/graphql/mutations/deleteStoreById.gql';
import UPDATE_PRIMARY_CONTACT_INFO from '@/graphql/mutations/updatePrimaryContactInfo.gql';
import UPDATE_ORGANIZATION_INFO from '@/graphql/mutations/updateOrganizationInfo.gql';
import UPDATE_HEADQUARTERS_INFO from '@/graphql/mutations/updateHeadquartersInfo.gql';
import UPDATE_HEADQUARTERS_SHIPPING_INFO from '@/graphql/mutations/updateHeadquartersShippingInfo.gql';
import CREATE_STORE from '@/graphql/mutations/createStore.gql';

//Error Handling
import AppState from '@/store/AppState';

const apollo = apolloProvider.defaultClient;
const httpApollo = httpApolloProvider.defaultClient; // Only for operations with uploads

// An interface for getting, querying, and mutating our data. All methods with GraphQL queries by default return cached values, but can be forced to re-fetch.
const Store = new Vue({
	data() {
		return {
			allStores: {},
			company: {},
			fetchedStores: false
		};
	},
	computed: {
		currentCompany() {
			// Check to see if the current company object has any data
			if (this.company && Object.keys(this.company).length === 0 && Object.keys(this.stores).length > 0) {
				const id = this.stores[Object.keys(this.stores)[0]].parentId;

				apollo
					.query({
						query: GET_COMPANY_BY_ID,
						fetchPolicy: 'network-only',
						variables: {
							id
						}
					})
					.then(res => {
						// Update our company data object
						this.setCompany(res.data.getCompanyById);
					})
					.catch(e => {
						// Returns a nicely formatted GraphQL error object. Cheers!
						const formattedError = AppState.showGeneralError(e);

						if (formattedError) {
							logger[formattedError.code === FORBIDDEN ? 'warn' : 'error'](
								{ error: (formattedError && formattedError.error) || e, companyId: id },
								'Failed to get company by ID'
							);
						} else {
							logger.error({ error: e, userId: id }, 'Failed to get company by ID');
						}

						// Set our company as null so that we know there was an error
						this.setCompany(null);
					});
			}
			return this.company;
		},

		// Returns the store specified by the ID
		store() {
			return id => {
				if (!this.allStores[id]) {
					// Start the fetch for the data!
					apollo
						.query({
							query: GET_STORE_BY_ID,
							variables: {
								id
							},
							fetchPolicy: 'network-only'
						})
						.then(response => {
							// Update our allStores data object
							this.setStore(id, response.data.getStoreById);
						})
						.catch(e => {
							// Returns a nicely formatted GraphQL error object. Cheers!
							const formattedError = AppState.showGeneralError(e);

							if (formattedError) {
								logger[formattedError.code === FORBIDDEN ? 'warn' : 'error'](
									{ error: (formattedError && formattedError.error) || e, storeId: id },
									'Failed to get store by ID'
								);
							} else {
								logger.error({ error: e, storeId: id }, 'Failed to get store by ID');
							}

							// Set our store as null so that we know there was an error
							this.setStore(id, null);
						});
				}

				return this.allStores[id];
			};
		},

		stores() {
			if (!this.fetchedUserStores) {
				// Start the fetch!
				apollo
					.query({
						query: GET_USER_STORES,
						fetchPolicy: 'network-only'
					})
					.then(response => {
						// Update our store data
						const results = response.data.getUserStores;

						// Successfully retrieved
						for (const store of results.stores) {
							this.setStore(store.id, store);
						}

						// Errors
						for (const storeId of results.errors) {
							this.setStore(storeId, null);
						}
					})
					.catch(e => {
						// Returns a nicely formatted GraphQL error object. Cheers!
						const formattedError = AppState.showGeneralError(e);
						logger.error({ error: (formattedError && formattedError.error) || e }, 'Failed to get user stores');

						// Try again next time
						this.$set(this, 'fetchedStores', false);
					});

				// Mark this flag so we don't accidentally do this fetch again
				this.$set(this, 'fetchedStores', true);
			}

			// Simple - grab and return all stores
			return this.allStores;
		}
	},
	methods: {
		setCompany(company) {
			const existing = this.company || {};
			this.$set(this, 'company', company ? { ...existing, ...company } : company);
		},

		// Set a store's details in our stores object
		setStore(id, store) {
			const existing = this.allStores[id] || {};
			this.$set(this.allStores, id, store ? { ...existing, ...store } : store);
		},

		// Remove a store from our stores object
		removeStore(id) {
			this.$delete(this.allStores, id);
		},

		// Creates a new store
		async createStore(storeInfo) {
			try {
				// Define the mutation
				const mutation = {
					mutation: CREATE_STORE,
					variables: {
						storeInfo
					}
				};

				// Call the mutation
				const {
					data: { createStore: store }
				} = await httpApollo.mutate(mutation);

				// Update our internal cached map of stores
				this.setStore(store.id, store);

				return { data: store };
			} catch (e) {
				// Returns a nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error({ storeInfo, error: (formattedError && formattedError.error) || e }, 'Error trying to create store');

				return formattedError;
			}
		},

		// Updates a store with the given ID and props
		async updateStoreInfo(id, props) {
			try {
				const {
					data: { updateStoreInfo: store }
				} = await httpApollo.mutate({
					mutation: UPDATE_STORE_INFO,
					variables: {
						id,
						props
					}
				});

				// Tell Vue about our update
				this.setStore(id, store);

				return { data: store };
			} catch (e) {
				// Returns a nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error({ storeId: id, error: (formattedError && formattedError.error) || e }, 'Error trying to update store');

				return formattedError;
			}
		},

		// Updates a store's shipping address with the given ID and shipping address details
		async updateShippingAddress(id, address) {
			try {
				const {
					data: { updateShippingAddress: store }
				} = await apollo.mutate({
					mutation: UPDATE_SHIPPING_ADDRESS,
					variables: {
						id,
						address
					}
				});

				// Tell Vue about our update
				this.setStore(id, store);

				return { data: store };
			} catch (e) {
				// Returns a nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error({ storeId: id, error: (formattedError && formattedError.error) || e }, 'Error trying to update shipping address');

				return formattedError;
			}
		},

		// Updates a store's billing address with the given ID and billing address details
		async updateBillingAddress(id, address) {
			try {
				const {
					data: { updateBillingAddress: store }
				} = await apollo.mutate({
					mutation: UPDATE_BILLING_ADDRESS,
					variables: {
						id,
						address
					}
				});

				// Tell Vue about our update
				this.setStore(id, store);

				return {
					data: store
				};
			} catch (e) {
				// Returns a nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error({ storeId: id, error: (formattedError && formattedError.error) || e }, 'Error trying to update billing address');

				return formattedError;
			}
		},

		// Delete's a store from Avana Wholesale based on the given ID
		async deleteStoreById(id) {
			try {
				const {
					data: { deleteStoreById: result }
				} = await apollo.mutate({
					mutation: DELETE_STORE_BY_ID,
					variables: {
						id
					}
				});

				// If we successfully deleted a store, than remove it from the stores list
				if (Object.keys(result).length) {
					this.removeStore(id);
				}

				return result;
			} catch (e) {
				// Returns a nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error({ storeId: id, error: (formattedError && formattedError.error) || e }, 'Error trying to delete store');

				return formattedError;
			}
		},

		// Updates a company's primary contact info and returns the new company object
		async updatePrimaryContactInfo(companyId, primaryContactInfo) {
			try {
				const {
					data: { updatePrimaryContactInfo: company }
				} = await apollo.mutate({
					mutation: UPDATE_PRIMARY_CONTACT_INFO,
					variables: {
						companyId,
						primaryContactInfo
					}
				});

				// Tell Vue about our update
				this.setCompany(company);

				return { data: company };
			} catch (e) {
				// Returns a nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error({ companyId, error: (formattedError && formattedError.error) || e }, 'Error trying to update primary contact info');

				return formattedError;
			}
		},

		// Updates a company's organization info and returns the new company object
		async updateOrganizationInfo(companyId, organizationInfo) {
			try {
				const {
					data: { updateOrganizationInfo: company }
				} = await httpApollo.mutate({
					mutation: UPDATE_ORGANIZATION_INFO,
					variables: {
						companyId,
						organizationInfo
					}
				});

				// Tell Vue about our update
				this.setCompany(company);

				return { data: company };
			} catch (e) {
				// Returns a nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error({ companyId, error: (formattedError && formattedError.error) || e }, 'Error trying to update organization info');

				return formattedError;
			}
		},

		// Updates a company's hq info and returns the new company object
		async updateHeadquartersInfo(companyId, headquartersInfo) {
			try {
				const {
					data: { updateHeadquartersInfo: company }
				} = await apollo.mutate({
					mutation: UPDATE_HEADQUARTERS_INFO,
					variables: {
						companyId,
						headquartersInfo
					}
				});

				// Tell Vue about our update
				this.setCompany(company);

				return { data: company };
			} catch (e) {
				// Returns a nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error({ companyId, error: (formattedError && formattedError.error) || e }, 'Error tying to update headquarter info');

				return formattedError;
			}
		},

		// Updates a company's hq shipping info and returns the new company object
		async updateHeadquartersShippingInfo(companyId, headquartersShippingInfo) {
			try {
				const {
					data: { updateHeadquartersShippingInfo: company }
				} = await apollo.mutate({
					mutation: UPDATE_HEADQUARTERS_SHIPPING_INFO,
					variables: {
						companyId,
						headquartersShippingInfo
					}
				});

				// Tell Vue about our update
				this.setCompany(company);

				return { data: company };
			} catch (e) {
				// Returns a nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error({ companyId, error: (formattedError && formattedError.error) || e }, 'Error tying to update headquarter shipping info');

				return formattedError;
			}
		},

		// A method that will accept incoming subscription payloads and do with them what needs to be done
		subscriptionHandler(payload) {
			switch (payload.action) {
				case 'createUser':
					// This should actually be handled by the User store
					EventBus.$emit('user', { action: 'create', user: payload.user });
					return;

				case 'updateCompany':
					this.setCompany(payload.company);
					return;

				case 'updateStore':
					this.setStore(payload.store.id, payload.store);
					return;

				case 'deleteStore':
					this.removeStore(payload.store.id);
					return;
			}
		},

		// Wipes everything out that we have available directly through our props
		clearAll() {
			Vue.set(this, 'allStores', {});
			Vue.set(this, 'company', {});
			Vue.set(this, 'fetchedStores', false);
		}
	},
	created() {
		EventBus.$on('store', this.subscriptionHandler);
		EventBus.$on('company', this.subscriptionHandler);
	}
});

export default Store;
